/**
 * Teamets loggøkt, regnet ut i stedet for avtalt.
 *
 * ========  PROBLEMET  ========
 *
 * Er man på et team, jobber man i samme logg — og rapporten henger på én
 * `log_session` gjennom `report.session_id`, så skal teamet dele rapport
 * MÅ de dele økt. Det er ikke en preferanse, det er datamodellen.
 *
 * Men appen er offline-først. To telefoner i en kjeller kan ikke avtale
 * hvilken `id` økta skal ha, og den som venter på nett for å få vite det
 * kan ikke logge. Å slå sammen i etterkant — flytte postene over når man
 * oppdager at noen andre alt hadde åpnet dagen — er den andre veien, og
 * den er full av kanter: hvem vant, hva med ankeret, hva med rapporten som
 * alt pekte på den tapende økta.
 *
 * ========  LØSNINGEN  ========
 *
 * De trenger ikke avtale den. Id-en UTLEDES av (produksjon, opptaksdag,
 * team), og da kommer to telefoner fram til nøyaktig den samme uten å ha
 * snakket sammen. En `upsert` blir idempotent, og sammenslåing finnes
 * ikke som problem.
 *
 * UUID v5 etter RFC 4122: navnebasert, SHA-1, stabil på tvers av språk og
 * kjøretider. Vi har ikke bruk for at den er kryptografisk sterk — vi har
 * bruk for at den er LIK overalt, hver gang.
 *
 * ========  UTEN TEAM  ========
 *
 * Da er loggen din egen, som før, og id-en lages tilfeldig på enheten.
 * `oektIdFor` returnerer null, og den som kaller lager sin egen. Det er
 * ikke et unntak som mangler — det er at en logg uten team ikke har noen
 * å dele med.
 */

const NAVNEROM = "9b1dbd4f-2f7c-4b6e-9d3d-7e2a1c5f8a40";

/* ---- SHA-1 ---------------------------------------------------------
 *
 * Skrevet ut for hånd fordi den må være SYNKRON. `crypto.subtle.digest`
 * er asynkron, og da ville hvert eneste sted som trenger en økt-id blitt
 * asynkront med — inkludert knappetrykket som fanger tidskoden.
 *
 * Det er ikke et sted å være kreativ. Testene kjører vektorene fra
 * RFC 3174 og RFC 4122.
 * ------------------------------------------------------------------- */

function roter(n: number, b: number): number {
  return ((n << b) | (n >>> (32 - b))) >>> 0;
}

function sha1(bytes: readonly number[]): number[] {
  const lengdeBits = bytes.length * 8;
  const m = [...bytes, 0x80];
  while (m.length % 64 !== 56) m.push(0);
  /* Lengden er 64 bit. De fire øverste byte-ene er null for alt vi kan
     komme til å hashe her — en uuid og en dagtekst. */
  m.push(0, 0, 0, 0,
    (lengdeBits >>> 24) & 0xff, (lengdeBits >>> 16) & 0xff,
    (lengdeBits >>> 8) & 0xff, lengdeBits & 0xff);

  let h0 = 0x67452301, h1 = 0xefcdab89, h2 = 0x98badcfe;
  let h3 = 0x10325476, h4 = 0xc3d2e1f0;

  for (let i = 0; i < m.length; i += 64) {
    const w = new Array<number>(80);
    for (let j = 0; j < 16; j++) {
      w[j] = ((m[i + j * 4] ?? 0) << 24) | ((m[i + j * 4 + 1] ?? 0) << 16)
        | ((m[i + j * 4 + 2] ?? 0) << 8) | (m[i + j * 4 + 3] ?? 0);
    }
    for (let j = 16; j < 80; j++) {
      w[j] = roter((w[j - 3] ?? 0) ^ (w[j - 8] ?? 0) ^ (w[j - 14] ?? 0) ^ (w[j - 16] ?? 0), 1);
    }

    let a = h0, b = h1, c = h2, d = h3, e = h4;
    for (let j = 0; j < 80; j++) {
      const [f, k] = j < 20 ? [(b & c) | (~b & d), 0x5a827999]
        : j < 40 ? [b ^ c ^ d, 0x6ed9eba1]
          : j < 60 ? [(b & c) | (b & d) | (c & d), 0x8f1bbcdc]
            : [b ^ c ^ d, 0xca62c1d6];
      const t = (roter(a, 5) + (f >>> 0) + e + k + (w[j] ?? 0)) >>> 0;
      e = d; d = c; c = roter(b, 30); b = a; a = t;
    }
    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0; h4 = (h4 + e) >>> 0;
  }

  const ut: number[] = [];
  for (const h of [h0, h1, h2, h3, h4]) {
    ut.push((h >>> 24) & 0xff, (h >>> 16) & 0xff, (h >>> 8) & 0xff, h & 0xff);
  }
  return ut;
}

export function sha1Hex(tekst: string): string {
  return sha1(tilBytes(tekst)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function tilBytes(s: string): number[] {
  const ut: number[] = [];
  for (const b of new TextEncoder().encode(s)) ut.push(b);
  return ut;
}

function uuidTilBytes(u: string): number[] {
  const rent = u.replace(/-/g, "");
  const ut: number[] = [];
  for (let i = 0; i < 32; i += 2) ut.push(parseInt(rent.slice(i, i + 2), 16));
  return ut;
}

/** UUID v5: navnebasert, SHA-1. RFC 4122 § 4.3. */
export function uuid5(navnerom: string, navn: string): string {
  const h = sha1([...uuidTilBytes(navnerom), ...tilBytes(navn)]);
  h[6] = ((h[6] ?? 0) & 0x0f) | 0x50;          // versjon 5
  h[8] = ((h[8] ?? 0) & 0x3f) | 0x80;          // variant RFC 4122
  const hex = h.slice(0, 16).map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}`
    + `-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

/**
 * Id-en teamets loggøkt har for en opptaksdag.
 *
 * Null når man ikke er på et team — da er loggen din egen, og den som
 * kaller lager en tilfeldig id som før.
 *
 * TEAMET ER MED I NØKKELEN, ikke bare produksjon og dag. LET 1 og LET 2
 * logger samme dag på samme produksjon og skal ha hver sin logg og hver
 * sin rapport.
 */
export function oektIdFor(
  produksjonId: string, opptaksdag: string, teamId: string | null,
): string | null {
  if (teamId === null || teamId === "") return null;
  return uuid5(NAVNEROM, `${produksjonId}:${opptaksdag}:${teamId}`);
}
